import React from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import IconButton, { IconButtonProps } from "@material-ui/core/IconButton";
import Grid from "@material-ui/core/Grid";
import CheckBoxOutlineBlankOutlinedIcon from "@material-ui/icons/CheckBoxOutlineBlankOutlined";
import CheckBoxOutlinedIcon from "@material-ui/icons/CheckBoxOutlined";
import clsx from "clsx";

import { ImageWithThumbnail } from "./ImageControl";
import { noop } from "../../../utils";
import LapiImg from "../../LapiImg";

const useStyles = makeStyles((theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    width: "100%",
    height: "100%",
    paddingTop: 32,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    zIndex: theme.zIndex.appBar - 1,
  },
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    maxWidth: "100%",
  },
  body: {
    flex: 1,
    padding: 8,
  },
  wrapperWithIntrinsicRatio: {
    position: "relative",
    backgroundColor: theme.palette.common.black,
    paddingBottom: "75%", // 4:3
    height: 0,
    "&:hover": {
      cursor: "all-scroll",
    },
  },
  elementToStretch: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
  checkboxContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    position: "absolute",
    top: 8,
    left: -28,
  },
  checkboxButton: {
    width: 56,
    padding: 0,
    color: theme.palette.primary.dark,
    "&:hover": {
      backgroundColor: "initial",
    },
    boxShadow: theme.shadows[2],
  },
  checkboxButtonSelected: {
    color: theme.palette.common.white,
  },
  checkboxIcon: {
    width: "100%",
    height: "100%",
    backgroundColor: theme.palette.common.white,
    borderRadius: "50%",
    padding: 8,
  },
  checkboxIconSelected: {
    backgroundColor: "green",
  },
  containerGrid: {
    justifyContent: "space-around",
  },
  itemGrid: {
    maxWidth: 450,
  },
}));

export type PreviewProps = {
  images: ImageWithThumbnail[];
  selectionDisabled: boolean;
  isSelected(img: ImageWithThumbnail): boolean;
  onActiveImageChange?: React.Dispatch<
    React.SetStateAction<ImageWithThumbnail | undefined>
  >;
  onCheckboxClick?: (img: ImageWithThumbnail) => IconButtonProps["onClick"];
};

function Preview(props: PreviewProps) {
  ///////////////// PROPS, HOOKS AND STATE SETUP////////////////////////////////

  const {
    images,
    selectionDisabled,
    isSelected,
    onCheckboxClick = noop,
  } = props;

  ///////////////// CSS ////////////////////////////////////////////////////////

  const classes = useStyles();

  return (
    <Container className={classes.container} maxWidth="md">
      <div className={classes.body}>
        <Grid container className={classes.containerGrid} spacing={4}>
          {images.map((img) => (
            <Grid
              key={img.image}
              item
              xs={12}
              sm={6}
              className={classes.itemGrid}
            >
              <div className={classes.wrapperWithIntrinsicRatio}>
                <LapiImg
                  className={classes.elementToStretch}
                  src={img.image}
                  size={500}
                  zoom
                  target={img.image}
                />
                <div className={classes.checkboxContainer}>
                  <IconButton
                    className={clsx(
                      classes.checkboxButton,
                      isSelected(img)
                        ? classes.checkboxButtonSelected
                        : undefined
                    )}
                    disabled={selectionDisabled && !isSelected(img)}
                    onClick={onCheckboxClick(img)}
                  >
                    {isSelected(img) ? (
                      <CheckBoxOutlinedIcon
                        className={clsx(
                          classes.checkboxIcon,
                          classes.checkboxIconSelected
                        )}
                      />
                    ) : (
                      <CheckBoxOutlineBlankOutlinedIcon
                        className={classes.checkboxIcon}
                      />
                    )}
                  </IconButton>
                </div>
              </div>
            </Grid>
          ))}
        </Grid>
      </div>
    </Container>
  );
}

export default Preview;
